import { faXmark, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { cardMessages } from 'src/features/cards/utils/messages';
import { MembershipGroupFormModal } from 'src/features/memberships/components/forms/MembershipGroupFormModal';
import { CellAllowedDays } from 'src/features/memberships/components/table/cells/CellAllowedDays';
import { CellAllowedPoints } from 'src/features/memberships/components/table/cells/CellAllowedPoints';
import { CellAllowedSubParkings } from 'src/features/memberships/components/table/cells/CellAllowedSubParkings';
import { CellLongText } from 'src/features/memberships/components/table/cells/CellLongText';
import { START_FULL_DAY, END_FULL_DAY, badgeAllowedDaysOptions } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { useListPoints } from 'src/features/points/api/useListPoints';
import { useListSubParkings } from 'src/features/subParkings/api/useListSubParkings';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    CellIndex,
    DataTable,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import CellDate from 'src/registry/components/table/cells/CellDate';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { Badge } from 'src/registry/ui/Badge';
import { generalMessages } from 'src/utils/messages';

import type { MembershipGroup } from 'src/features/memberships/types';
import type useFilters from 'src/registry/hooks/useFilters';
import type { BadgeOptions, ListResponseSmartParkAPI } from 'src/types';
const MEMBERSHIP_GROUPS_PANEL_NAME = 'membership-groups-panel';

interface MembershipGroupsTableProps {
    membershipGroups?: ListResponseSmartParkAPI<MembershipGroup>;
    isLoadingMembershipGroups?: boolean;
    isErrorMembershipGroups?: boolean;
    refetchMembershipGroups?: () => void;
    sorting: Omit<ReturnType<typeof useSorting>, 'querySortingOptions'>;
    pagination: Omit<ReturnType<typeof usePagination>, 'queryPaginationOptions'>;
    search?: Omit<ReturnType<typeof useSearch>, 'querySearchOptions'>;
    filters?: Omit<ReturnType<typeof useFilters>, 'queryFiltersOptions'>;
}
const MembershipGroupsTable = ({
    membershipGroups,
    isLoadingMembershipGroups,
    isErrorMembershipGroups,
    refetchMembershipGroups,
    sorting,
    pagination,
    search,
    filters,
}: MembershipGroupsTableProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const permissionManage = useHasPermissions([PERMISSION.VEHICLE_MEMBERSHIP_DETAILS_MANAGE]);

    const navigate = useNavigate();

    const rowSelection = useRowSelection({ sidePanelName: MEMBERSHIP_GROUPS_PANEL_NAME });

    const columnHelper = createColumnHelper<MembershipGroup>();

    const { data: points, isLoading: isLoadingPoints } = useListPoints({ parkingId });

    const { data: subParkings, isLoading: isLoadingSubParkings } = useListSubParkings({ parkingId });

    const inParkingBadgeOptions: BadgeOptions = {
        true: {
            theme: 'green',
            label: generalMessages.yes,
            icon: faCheck,
        },
        false: {
            theme: 'red',
            label: generalMessages.no,
            icon: faXmark,
        },
    };

    const columns = [
        columnHelper.accessor('id', {
            header: DataTableColumnGenericHeaderDefinition,
            cell: CellIndex,
            maxSize: 45,
            meta: { title: '#' },
        }),
        columnHelper.accessor('name', {
            header: (props) => <DataTableColumnHeader title={formatMessage(generalMessages.name)} {...props} />,
            enableSorting: true,
            enableHiding: true,
            cell: (props) => <CellLongText {...props} className="max-w-32" />,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: (props) => <DataTableColumnHeader title={formatMessage(membershipMessages.validFrom)} {...props} />,
            cell: (props) => (
                <CellDate {...props} dateTimeFormatOptions={{ day: 'numeric', month: 'short', year: 'numeric' }} />
            ),
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.DATE,
                inlineDateFilter: true,
                dateFilter: {
                    afterDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                customDropdownMenuLabel: formatMessage(membershipMessages.filterByActivePeriod),
                title: formatMessage(membershipMessages.validFrom),
                filterLabel: formatMessage(membershipMessages.activePeriod),
            },
            id: 'inline_date_filters',
        }),
        columnHelper.accessor('valid_until', {
            header: (props) => (
                <DataTableColumnHeader title={formatMessage(membershipMessages.validUntil)} {...props} />
            ),
            cell: (props) => {
                const year = new Date(props.row.original.valid_until).getFullYear();
                // We handle any year after 2100 as indefinite
                if (year >= 2100) return formatMessage(generalMessages.indefinite);
                return (
                    <CellDate {...props} dateTimeFormatOptions={{ day: 'numeric', month: 'short', year: 'numeric' }} />
                );
            },
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(membershipMessages.validUntil),
            },
        }),
        columnHelper.display({
            header: formatMessage(membershipMessages.allowedHours),
            id: 'allowed_hours',
            enableHiding: true,
            cell: ({ row }) => {
                if (
                    row.original.allowed_hours_start === START_FULL_DAY &&
                    row.original.allowed_hours_end === END_FULL_DAY
                )
                    return formatMessage(membershipMessages.fullDay);
                return (
                    <div className="flex flex-wrap">
                        <span className="whitespace-nowrap">{row.original.allowed_hours_start}</span>
                        <span> - </span>
                        <span className="whitespace-nowrap">{row.original.allowed_hours_end}</span>
                    </div>
                );
            },
        }),
        columnHelper.accessor('allowed_days', {
            header: DataTableColumnHeader,
            cell: CellAllowedDays,
            enableHiding: true,
            meta: {
                title: formatMessage(membershipMessages.allowedDays),
                badgeOptions: badgeAllowedDaysOptions,
            },
        }),

        columnHelper.accessor('allowed_parkings', {
            header: DataTableColumnHeader,
            cell: (props) => (
                <CellAllowedSubParkings
                    subParkings={subParkings?.results}
                    isLoadingSubParkings={isLoadingSubParkings}
                    {...props}
                />
            ),
            enableColumnFilter: true,
            enableHiding: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(membershipMessages.allowedParkings),
                options: subParkings?.results.map((zone) => ({ value: zone.id, label: zone.name })),
            },
            id: 'parking_id',
        }),

        columnHelper.accessor('allowed_points', {
            header: DataTableColumnHeader,
            cell: (props) => (
                <CellAllowedPoints points={points?.results} isLoadingPoints={isLoadingPoints} {...props} />
            ),
            enableHiding: true,
            meta: {
                title: formatMessage(membershipMessages.allowedPoints),
            },
        }),
        columnHelper.accessor('allowed_parking_places', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(membershipMessages.allowedParkingPlaces),
            },
        }),
        columnHelper.accessor('reserve_place', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const badgeText = row.original.reserve_place ? generalMessages.yes : generalMessages.no;
                const iconColor = row.original.reserve_place ? 'bg-emerald-600' : 'bg-red-600';
                return (
                    <Badge>
                        <div className={`size-1.5 rounded-full ${iconColor}`}></div>
                        {formatMessage(badgeText)}
                    </Badge>
                );
            },
            enableHiding: true,
            meta: {
                title: formatMessage(membershipMessages.reservedPlace),
            },
        }),
        columnHelper.accessor('in_parking', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(membershipMessages.inParking),
                badgeOptions: inParkingBadgeOptions,
                filterLabel: formatMessage(membershipMessages.inParking),
            },
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={membershipGroups}
            refetch={refetchMembershipGroups}
            pagination={pagination}
            sorting={sorting}
            filters={filters}
            rowSelection={rowSelection}
            isError={isErrorMembershipGroups}
            isLoading={isLoadingMembershipGroups}
            semantics={{
                title: formatMessage(membershipMessages.membershipGroups),
                entryName: formatMessage(membershipMessages.membership),
                entryNames: formatMessage(membershipMessages.memberships),
                placeholder: formatMessage(cardMessages.nameOrCardNumber),
            }}
            getRowId={(row) => row.id.toString()}
            components={{
                newEntry: permissionManage ? MembershipGroupFormModal : undefined,
                pagination: DataTablePagination,
                controls: DataTableControls,
            }}
            // This overrides the sidepanel when not browsing as a client
            dataTableBodyProps={{
                onRowClickCallback: (row) => {
                    void navigate(`/vehicles/memberships/${row.id}`);
                },
            }}
            search={search}
        />
    );
};

export { MembershipGroupsTable };
